import createClient from 'openapi-fetch';
import type { paths } from './types';

export const CLIENT_VERSION = '0.2.0';
export const TOKEN_STORAGE_KEY = 'priceloss_auth_tokens';
let sessionGeneration = 0;
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === TOKEN_STORAGE_KEY || event.key === null) sessionGeneration++;
  });
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType?: string;
}

export function getStoredTokens(): AuthTokens | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

function persistTokens(tokens: AuthTokens): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

export function setStoredTokens(tokens: AuthTokens): void {
  sessionGeneration++;
  persistTokens(tokens);
}

export function clearStoredTokens(): void {
  sessionGeneration++;
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

let activeRefreshPromise: Promise<string | null> | null = null;

/**
 * Mutex-locked token refresh runner.
 * Ensures only ONE refresh call is in flight across concurrent requests.
 */
export async function refreshAccessToken(baseUrl: string = ''): Promise<string | null> {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = (async () => {
    const tokens = getStoredTokens();
    const generation = sessionGeneration;
    try {
      if (!tokens?.refreshToken) {
        clearStoredTokens();
        return null;
      }

      const refreshUrl = `${baseUrl}/api/v1/auth/refresh`;
      const response = await fetch(
        new Request(refreshUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-PriceLoss-Client-Version': CLIENT_VERSION,
          },
          body: JSON.stringify({
            refresh_token: tokens.refreshToken,
          }),
        })
      );

      if (generation !== sessionGeneration || getStoredTokens()?.refreshToken !== tokens.refreshToken) return null;
      if (!response.ok) {
        clearStoredTokens();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('priceloss:session-expired'));
        }
        return null;
      }

      const data = (await response.json()) as {
        access_token: string;
        refresh_token: string;
        token_type: string;
        expires_in: number;
      };

      if (generation !== sessionGeneration || getStoredTokens()?.refreshToken !== tokens.refreshToken) return null;

      const updatedTokens: AuthTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenType: data.token_type,
        expiresAt: Date.now() + data.expires_in * 1000,
      };

      persistTokens(updatedTokens);
      return data.access_token;
    } catch {
      if (generation === sessionGeneration && tokens && getStoredTokens()?.refreshToken === tokens.refreshToken) clearStoredTokens();
      return null;
    } finally {
      activeRefreshPromise = null;
    }
  })();

  return activeRefreshPromise;
}

/**
 * Factory creating an openapi-fetch client with auth & version interceptors.
 */
export function createApiClient(baseUrl: string = '') {
  const customFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    let req: Request;
    if (input instanceof Request) {
      req = input;
    } else {
      req = new Request(input, init);
    }

    const generation = sessionGeneration;
    const tokensAtStart = getStoredTokens();
    let bodyData: ArrayBuffer | undefined;
    if (req.body && req.method !== 'GET' && req.method !== 'HEAD') {
      bodyData = await req.clone().arrayBuffer();
    }

    // 1. Inject client version header
    const headers = new Headers(req.headers);
    if (!headers.has('X-PriceLoss-Client-Version')) {
      headers.set('X-PriceLoss-Client-Version', CLIENT_VERSION);
    }

    // 2. Inject authorization header if present in storage and not already set
    if (!headers.has('Authorization')) {
      const tokens = tokensAtStart;
      if (tokens?.accessToken) {
        headers.set('Authorization', `Bearer ${tokens.accessToken}`);
      }
    }

    const modifiedReq = new Request(req, {
      method: req.method,
      headers,
      body: bodyData,
      // @ts-ignore
      duplex: bodyData ? 'half' : undefined,
    });
    let response = await fetch(modifiedReq);

    // 3. Handle 426 UPGRADE_REQUIRED
    if (response.status === 426) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('priceloss:upgrade-required'));
      }
      return response;
    }

    // 4. Handle 401 UNAUTHORIZED with transparent token refresh
    const isAuthEndpoint =
      req.url.includes('/api/v1/auth/login') ||
      req.url.includes('/api/v1/auth/register') ||
      req.url.includes('/api/v1/auth/refresh');

    if (response.status === 401 && !isAuthEndpoint && !req.headers.has('Authorization') && !req.signal.aborted && generation === sessionGeneration) {
      const newAccessToken = await refreshAccessToken(baseUrl);
      if (newAccessToken && generation === sessionGeneration && !req.signal.aborted) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);

        const retryReq = new Request(req, {
          method: req.method,
          headers: retryHeaders,
          body: bodyData,
          // @ts-ignore
          duplex: bodyData ? 'half' : undefined,
        });
        response = await fetch(retryReq);
      }
    }

    return response;
  };

  return createClient<paths>({
    baseUrl,
    fetch: customFetch as (input: Request) => Promise<Response>,
  });
}

export const apiClient = createApiClient();
